/**
 * FERP PDF Metadata Panel (DRR-079)
 *
 * Displays PDF title, author, creator, page count via pdfjs getMetadata().
 */

import { useEffect, useState } from 'react';
import { Stack, Box, Text, Loader, Table } from '@mantine/core';
import type { PdfMetadata } from './types';

interface MetadataPanelProps {
  pdfDocumentProxy?: unknown;
}

export function MetadataPanel({ pdfDocumentProxy }: MetadataPanelProps) {
  const [metadata, setMetadata] = useState<PdfMetadata | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pdfDocumentProxy) return;
    setLoading(true);

    const proxy = pdfDocumentProxy as {
      getMetadata: () => Promise<{
        info: Record<string, string>;
        metadata: unknown;
      }>;
      numPages: number;
      getPage: (n: number) => Promise<{ getViewport: (opts: { scale: number }) => { width: number; height: number } }>;
    };

    (async () => {
      try {
        const { info } = await proxy.getMetadata();
        const firstPage = await proxy.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1 });

        setMetadata({
          title: info['Title'] || undefined,
          author: info['Author'] || undefined,
          subject: info['Subject'] || undefined,
          creator: info['Creator'] || undefined,
          producer: info['Producer'] || undefined,
          creationDate: info['CreationDate'] || undefined,
          modDate: info['ModDate'] || undefined,
          pageCount: proxy.numPages,
          pageSize: { width: viewport.width, height: viewport.height },
        });
      } catch {
        setMetadata(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [pdfDocumentProxy]);

  if (loading) {
    return (
      <Box p="sm" ta="center">
        <Loader size="sm" />
      </Box>
    );
  }

  if (!metadata) {
    return (
      <Box p="sm">
        <Text size="xs" c="dimmed">No metadata available</Text>
      </Box>
    );
  }

  const rows: [string, string | undefined][] = [
    ['Title', metadata.title],
    ['Author', metadata.author],
    ['Subject', metadata.subject],
    ['Creator', metadata.creator],
    ['Producer', metadata.producer],
    ['Pages', String(metadata.pageCount)],
    ['Page Size', metadata.pageSize ? `${Math.round(metadata.pageSize.width)} × ${Math.round(metadata.pageSize.height)} pt` : undefined],
    ['Created', metadata.creationDate],
    ['Modified', metadata.modDate],
  ];

  return (
    <Stack gap={0} p="xs">
      <Table verticalSpacing={4} horizontalSpacing={4}>
        <Table.Tbody>
          {rows
            .filter(([, val]) => val)
            .map(([label, value]) => (
              <Table.Tr key={label}>
                <Table.Td>
                  <Text size="xs" fw={500} c="dimmed">{label}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" lineClamp={2}>{value}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
